import { colorHexToNumArray, renderPalette } from './utils';
import * as WeatherLayers from 'weatherlayers-gl';
import type { TextureData } from 'weatherlayers-gl';
import { ClipExtension } from '@deck.gl/extensions';
import {
  ContourLayerSettings,
  GridLayerSettings,
  HighLowLayerSettings,
  MapLayerSettings,
  ParticleLayerSettings,
  RasterLayerSettings,
} from '@app/api/mapping/types';
import { BufferedFrameCache } from './caching';

interface ImageProps {
  image: TextureData;
  imageType: 'VECTOR' | 'SCALAR';
  imageUnscale: [number, number];
  bounds: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  image2?: TextureData;
  imageWeight?: number;
}

function getImagePropsForVariable(
  variable: string,
  frameCache: BufferedFrameCache,
  frameIndex: number,
): ImageProps | null {
  const frame = frameCache.frames[frameIndex];
  const current = frame.variableData?.[variable];

  if (current != null) {
    return {
      image: current.texture,
      imageType: current.imageType,
      imageUnscale: current.imageUnscale,
      bounds: current.bounds,
    };
  }

  const prevIndex = frame.previousReadyFrame;
  const nextIndex = frame.nextReadyFrame;
  if (prevIndex == null || nextIndex == null) {
    return null;
  }

  const prevData = frameCache.frames[prevIndex].variableData?.[variable];
  const nextData = frameCache.frames[nextIndex].variableData?.[variable];
  if (prevData == null || nextData == null) {
    return null;
  }

  const denom = nextIndex - prevIndex;
  if (denom <= 0) {
    return null;
  }
  const imageWeight = (frameIndex - prevIndex) / denom;

  return {
    image: prevData.texture,
    image2: nextData.texture,
    imageWeight,
    imageType: prevData.imageType,
    imageUnscale: prevData.imageUnscale,
    bounds: prevData.bounds,
  };
}

export function renderRasterLayer(
  layer: RasterLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
  pickable: boolean = true,
): WeatherLayers.RasterLayer | null {
  // Use custom color scale if provided, otherwise use defaults
  const img = getImagePropsForVariable(layer.variable, frameCache, frameIndex);
  if (img == null) return null;
  const minValue = layer.colorScaleMin ?? img.imageUnscale[0];
  const maxValue = layer.colorScaleMax ?? img.imageUnscale[1];

  return new WeatherLayers.RasterLayer({
    beforeId: 'waterway_label', // In interleaved mode render the layer under map labels
    id: layer.id,
    image: img.image,
    image2: img.image2,
    imageWeight: img.imageWeight,
    imageType: img.imageType, // "SCALAR",
    imageUnscale: img.imageUnscale, // [180.0, 325.15],
    imageMinValue: layer.minValue,
    imageMaxValue: layer.maxValue,
    imageInterpolation: layer.interpolation,
    imageSmoothing: 0, // Disable smoothing for sharper rendering
    bounds: img.bounds,
    palette: renderPalette(minValue, maxValue, layer.palette),
    extensions: [new ClipExtension()],
    clipBounds: [-181, -85.051129, 181, 85.051129],
    opacity: layer.opacity,
    visible: true,
    pickable: pickable,
  });
}

export function renderContourLayer(
  layer: ContourLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
): WeatherLayers.ContourLayer | null {
  const img = getImagePropsForVariable(layer.variable, frameCache, frameIndex);
  if (img == null) return null;
  return new WeatherLayers.ContourLayer({
    beforeId: 'waterway_label', // In interleaved mode render the layer under map labels
    id: layer.id,
    image: img.image,
    image2: img.image2,
    imageWeight: img.imageWeight,
    imageType: img.imageType, // "SCALAR",
    imageUnscale: img.imageUnscale, // [180.0, 325.15],
    imageSmoothing: 4,
    imageInterpolation: layer.interpolation,
    imageMinValue: layer.minValue,
    imageMaxValue: layer.maxValue,
    bounds: img.bounds,
    extensions: [new ClipExtension()],
    clipBounds: [-181, -85.051129, 181, 85.051129],
    color: colorHexToNumArray(layer.color),
    width: layer.width,
    interval: layer.interval,
    majorInterval: layer.majorInterval,
    opacity: layer.opacity,
  });
}

export function renderGridLayer(
  layer: GridLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
): WeatherLayers.GridLayer | null {
  const img = getImagePropsForVariable(layer.variable, frameCache, frameIndex);
  if (img == null) return null;
  return new WeatherLayers.GridLayer({
    beforeId: 'waterway_label', // In interleaved mode render the layer under map labels
    id: layer.id,
    image: img.image,
    image2: img.image2,
    imageWeight: img.imageWeight,
    imageType: img.imageType, // "SCALAR",
    imageUnscale: img.imageUnscale, // [180.0, 325.15],
    imageSmoothing: 0,
    imageInterpolation: 'NEAREST',
    imageMinValue: null, // layer.minValue,
    imageMaxValue: null, // layer.maxValue,
    bounds: img.bounds,
    extensions: [new ClipExtension()],
    clipBounds: [-181, -85.051129, 181, 85.051129],
    textColor: colorHexToNumArray(layer.textColor),
    textOutlineColor: colorHexToNumArray(layer.textOutlineColor),
    textSize: layer.textSize,
    density: -2,
    style: layer.style,
    opacity: layer.opacity,
    visible: true,
    palette: null,
    getPolygonOffset: () => [0, -1000],
  });
}

export function renderHighLowLayer(
  layer: HighLowLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
): WeatherLayers.HighLowLayer | null {
  const img = getImagePropsForVariable(layer.variable, frameCache, frameIndex);
  if (img == null) return null;
  return new WeatherLayers.HighLowLayer({
    beforeId: 'waterway_label', // In interleaved mode render the layer under map labels
    id: layer.id,
    image: img.image,
    image2: img.image2,
    imageWeight: img.imageWeight,
    imageType: img.imageType, // "SCALAR",
    imageUnscale: img.imageUnscale, // [94860, 104580],
    imageInterpolation: layer.interpolation,
    imageMinValue: layer.minValue ?? null,
    imageMaxValue: layer.maxValue ?? null,
    imageSmoothing: 4,
    bounds: img.bounds,
    extensions: [new ClipExtension()],
    clipBounds: [-181, -85.051129, 181, 85.051129],
    visible: true,
    radius: layer.radius,
    textSize: layer.textSize,
    textColor: colorHexToNumArray(layer.textColor),
    textOutlineWidth: layer.textOutlineWidth,
    textOutlineColor: colorHexToNumArray(layer.textOutlineColor),
    opacity: layer.opacity,
    getPolygonOffset: () => [0, -1000],
    unitFormat: layer.legend?.unitFormat ?? {
      unit: '',
    },
  });
}

export function renderParticleLayer(
  layer: ParticleLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
): WeatherLayers.ParticleLayer | null {
  const img = getImagePropsForVariable(layer.variable, frameCache, frameIndex);
  if (img == null) return null;
  return new WeatherLayers.ParticleLayer({
    id: layer.id,
    image: img.image,
    image2: img.image2,
    imageWeight: img.imageWeight,
    imageType: img.imageType, // "VECTOR",
    imageUnscale: img.imageUnscale, // [-128, 127],
    imageInterpolation: layer.interpolation,
    imageMinValue: layer.minValue,
    imageMaxValue: layer.maxValue,
    bounds: img.bounds,
    extensions: [new ClipExtension()],
    clipBounds: [-181, -85.051129, 181, 85.051129],
    visible: true,
    numParticles: layer.numParticles,
    maxAge: layer.maxAge,
    speedFactor: layer.speedFactor,
    animate: layer.animate,
    opacity: layer.opacity,
  });
}

export function renderLayer(
  layer: MapLayerSettings,
  frameCache: BufferedFrameCache,
  frameIndex: number,
  pickable: boolean = true,
) {
  if (!layer.enabled) {
    return null;
  }

  switch (layer.kind) {
    case 'RASTER':
      return renderRasterLayer(layer, frameCache, frameIndex, pickable);
    case 'CONTOUR':
      return renderContourLayer(layer, frameCache, frameIndex);
    case 'GRID':
      return renderGridLayer(layer, frameCache, frameIndex);
    case 'HIGHLOW':
      return renderHighLowLayer(layer, frameCache, frameIndex);
    case 'PARTICLE':
      return renderParticleLayer(layer, frameCache, frameIndex);
    default:
      return null;
  }
}

export function renderLayerSet(
  layers: MapLayerSettings[],
  frameCache: BufferedFrameCache,
  frameIndex: number,
  isAnimationPlaying: boolean = false,
  isDragging: boolean = false,
  isLoading: boolean = false,
) {
  let isFirstRasterLayer = true;

  return layers
    .map((layer) => {
      // Skip HighLow layers during animation, dragging, or loading
      if (layer.kind === 'HIGHLOW' && (isAnimationPlaying || isDragging || isLoading)) {
        return null;
      }

      const isRasterLayer = layer.kind === 'RASTER';
      const shouldBePickable = isRasterLayer ? isFirstRasterLayer : true;

      if (isRasterLayer) {
        isFirstRasterLayer = false;
      }

      return renderLayer(layer, frameCache, frameIndex, shouldBePickable);
    })
    .filter((x) => x != null);
}
